import { useEffect } from "react";
import { createPortal } from "react-dom";

interface Props {
  src: string;
  caption?: string;
  onClose: () => void;
  onPrev?: (() => void) | null;
  onNext?: (() => void) | null;
}

/**
 * Full-screen image viewer. Click outside, press Escape, or use arrow keys
 * to navigate between screenshots.
 *
 * Rendered via a React portal to `document.body` so the modal escapes any
 * ancestor that creates a containing block for fixed-positioned descendants
 * (e.g. our `<main>` element which has a fade-in transform that even at rest
 * is `matrix(1,0,0,1,0,0)` — non-none, so `position: fixed` would otherwise
 * pin to the page rather than the viewport, scrolling the modal off-screen).
 */
export function Lightbox({ src, caption, onClose, onPrev, onNext }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && onPrev) onPrev();
      else if (e.key === "ArrowRight" && onNext) onNext();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  return createPortal(
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div
        className="relative flex max-h-[92vh] max-w-[92vw] flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 201,
        }}
      >
        <img
          src={src}
          alt={caption || ""}
          onError={(e) => {
            // Source vanished (deleted job, missing screenshot id) — show a
            // soft fallback rather than nothing.
            const img = e.currentTarget;
            img.style.opacity = "0.4";
            img.alt = "Screenshot no longer available";
          }}
          className="max-h-[88vh] max-w-[92vw] rounded-md border border-white/10 object-contain shadow-2xl"
        />
        {caption && (
          <div className="mt-2 text-center text-sm text-white/80">{caption}</div>
        )}
        <div className="absolute right-2 top-2 flex gap-2">
          <a
            href={src}
            target="_blank"
            rel="noreferrer"
            className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-medium text-white backdrop-blur hover:bg-white/20"
            onClick={(e) => e.stopPropagation()}
          >
            Open in tab
          </a>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-medium text-white backdrop-blur hover:bg-white/20"
            aria-label="Close"
          >
            Close (Esc)
          </button>
        </div>
        {onPrev && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white backdrop-blur transition hover:bg-white/20"
            aria-label="Previous"
          >
            ‹
          </button>
        )}
        {onNext && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white backdrop-blur transition hover:bg-white/20"
            aria-label="Next"
          >
            ›
          </button>
        )}
      </div>
    </>,
    document.body,
  );
}
